/* One load of the portfolio, shared by every view.

   Services, assets, tasks and people are a few hundred rows in total and every
   view needs most of them -- the heatmap needs owners, the board needs tasks,
   the task list needs object names. Fetching per view would mean four
   near-identical loading states and a stale sidebar after every edit. So it is
   loaded once here and refreshed after each mutation.

   `refresh()` re-fetches everything rather than patching the item that
   changed. At this size that costs a few milliseconds and removes the entire
   class of bug where a dependency edit updates one screen and not another. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  api,
  type Bet,
  type Initiative,
  type Meta,
  type Person,
  type Task,
  type TrackedObject,
} from "./api";

interface Store {
  meta: Meta | null;
  services: TrackedObject[];
  assets: TrackedObject[];
  objects: TrackedObject[];
  tasks: Task[];
  people: Person[];
  bets: Bet[];
  initiatives: Initiative[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /* Wraps a mutation: surfaces its error in the banner instead of the console,
     and refreshes on success. Every write in the app goes through it. */
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
}

const Ctx = createContext<Store | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [services, setServices] = useState<TrackedObject[]>([]);
  const [assets, setAssets] = useState<TrackedObject[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [bets, setBets] = useState<Bet[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [m, s, a, t, p, b, i] = await Promise.all([
        api.meta(),
        api.list("services"),
        api.list("assets"),
        api.tasks(),
        api.people(),
        api.bets(),
        api.initiatives(),
      ]);
      setMeta(m);
      setServices(s);
      setAssets(a);
      setTasks(t);
      setPeople(p);
      setBets(b);
      setInitiatives(i);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
      try {
        const result = await action();
        setError(null);
        await refresh();
        return result;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return undefined;
      }
    },
    [refresh],
  );

  const value = useMemo<Store>(
    () => ({
      meta,
      services,
      assets,
      objects: [...services, ...assets],
      tasks,
      people,
      bets,
      initiatives,
      loading,
      error,
      refresh,
      run,
    }),
    [meta, services, assets, tasks, people, bets, initiatives, loading, error, refresh, run],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const store = useContext(Ctx);
  if (!store) throw new Error("useStore outside StoreProvider");
  return store;
}
